import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNow } from '@hooks/register/use-now';
import { useGoogleTagManager } from '@hooks/use-google-tag-manager';
import { useLocalize } from '@hooks/use-localize';
import { useStylesheet } from '@hooks/use-stylesheet';
import { createNowFormSchema } from '@views/register/now.schema';
import { useNavigate } from 'react-router';
import type { INowFormValues, INowModel, INowSubmitResult, ISelectOption } from '@/types/now';
import DynamicNow from '@views/register/now-sections/dynamic-now';
import StandardNow from '@views/register/now-sections/standard-now';
import NameConfirmationModal from '@views/register/name-confirmation-modal';

const DYNAMIC_REG_STYLESHEET = '/Content/css/referral-registration.css';

const defaultValues: INowFormValues = {
    firstName: __ENVIRONMENT__ == 'DEVELOPMENT' ? 'TestyDLA' : '',
    lastName: __ENVIRONMENT__ == 'DEVELOPMENT' ? 'TesterDLA' : '',
    isResidingInUsa: __ENVIRONMENT__ == 'DEVELOPMENT' ? '1' : '',
    countryOfResidenceId: '',
    internationalPhoneNumber: '',
    addressLine1: __ENVIRONMENT__ == 'DEVELOPMENT' ? '100 Crocus Lane' : '',
    city: __ENVIRONMENT__ == 'DEVELOPMENT' ? '' : '',
    state: __ENVIRONMENT__ == 'DEVELOPMENT' ? '' : '',
    postalCode: __ENVIRONMENT__ == 'DEVELOPMENT' ? '06851' : '',
    mobileAuthPhone: __ENVIRONMENT__ == 'DEVELOPMENT' ? '7003334444' : '',
    email: __ENVIRONMENT__ == 'DEVELOPMENT' ? 'tt-dla@me.com' : '',
    donationType: __ENVIRONMENT__ == 'DEVELOPMENT' ? '0' : '',
    agreedToHipaa: __ENVIRONMENT__ == 'DEVELOPMENT' ? true : null,
    connectToDonorIsDesired: __ENVIRONMENT__ == 'DEVELOPMENT' ? '' : '',
};

interface IProps {
    fromMicrosite: boolean
}

const Now = ({ fromMicrosite }: IProps) => {
    const { model, isLoading, error, submit, isSubmitting, submitError } = useNow(fromMicrosite);

    if (isLoading) {
        return (
            <div className="row">
                <div className="col-xs-12">Loading&hellip;</div>
            </div>
        );
    }

    const form = (
        <NowForm
            model={model}
            submit={submit}
            isSubmitting={isSubmitting}
            submitError={submitError}
        />
    );

    return (
        <>
            {error && (
                <div className="row">
                    <div className="col-xs-12">
                        <div className="alert alert-danger">{error}</div>
                    </div>
                </div>
            )}
            {model.recaptchaKey ? (
                <GoogleReCaptchaProvider
                    reCaptchaKey={model.recaptchaKey}
                    language={model.language}
                >
                    {form}
                </GoogleReCaptchaProvider>
            ) : (
                form
            )}
        </>
    );
};

interface INowFormProps {
    model: INowModel;
    submit: (values: INowFormValues, recaptchaToken: string) => Promise<INowSubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

const NowForm = ({ model, submit, isSubmitting, submitError }: INowFormProps) => {
    const { executeRecaptcha } = useGoogleReCaptcha();

    const navigate = useNavigate();

    const t = useLocalize(model.languageData);

    useStylesheet(DYNAMIC_REG_STYLESHEET, model.hasDynamicRegPage);

    const schema = useMemo(
        () => createNowFormSchema({ isDashLiverAvailable: model.isDashLiverAvailable }),
        [model.isDashLiverAvailable],
    );

    const methods = useForm<INowFormValues>({
        defaultValues: { ...defaultValues, ...model.values },
        resolver: zodResolver(schema),
    });

    const { handleSubmit, watch, getValues, setValue, setError } = methods;

    const [showNameModal, setShowNameModal] = useState(false);

    // Only US residents give a US mobile, so only they can be sent a verification text.
    const isUsResident = watch('isResidingInUsa') === '1';

    useEffect(() => {
        Object.entries(model.errors ?? {}).forEach(([field, message]) => {
            setError(field as keyof INowFormValues, { type: 'server', message });
        });
    }, [model.errors, setError]);

    useEffect(() => {
        if (!isUsResident) {
            setValue('addressLine1', '');
            setValue('postalCode', '');
            setValue('city', '');
            setValue('state', '0');
            setValue('mobileAuthPhone', '');
        }
    }, [isUsResident]);

    useGoogleTagManager(model.googleAnalyticsCode);

    const onValid = () => setShowNameModal(true);

    const handleConfirmName = async () => {
        setShowNameModal(false);

        const token = executeRecaptcha ? await executeRecaptcha('homepage') : '';
        const result = await submit(getValues(), token);

        // Surface server-side field errors through the same channel as client validation.
        if (!result.ok && result.errors) {
            Object.entries(result.errors).forEach(([field, message]) => {
                setError(field as keyof INowFormValues, { type: 'server', message });
            });
        }

        if (result.ok) {
            // if (isUsResident) {
            navigate('/register/auth-validate/' + result.token);
            // } else {
            //     navigate('/register/thank-you?id=' + result.registrationId + '&Key=' + result.editKey)
            // }
        }
    };

    const optionLabel = (options: ISelectOption[], value: string) => options.find((option) => option.value === value)?.label ?? '';

    // US residents confirm their address; everyone else confirms country of residence.
    const confirmation = {
        name: [getValues('firstName').trim(), getValues('lastName').trim()].filter(Boolean).join(' '),
        email: getValues('email').trim(),
        phone: isUsResident ? getValues('mobileAuthPhone').trim() : getValues('internationalPhoneNumber').trim(),
        address: isUsResident ?
            [
                getValues('addressLine1').trim(),
                getValues('city').trim(),
                optionLabel(model.states, getValues('state')),
                getValues('postalCode').trim(),
            ]
                .filter(Boolean)
                .join(', ')
            :
            optionLabel(model.countries, getValues('countryOfResidenceId')),
    };

    const alerts = (
        <>
            {model.isIE && (
                <div className="col-xs-12">
                    <div className="alert alert-warning">
                        {t(
                            'Alert_BrowserRecommend',
                            'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                        )}
                        .
                    </div>
                </div>
            )}

            {!model.isModelValid && (
                <div className="col-xs-12">
                    <div className="alert alert-danger validation-summary">
                        {t(
                            'Alert_PleaseCompleteRequiredFields',
                            'Please complete the required fields indicated below',
                        )}
                    </div>
                </div>
            )}

            <noscript>
                <div className="col-xs-12">
                    <div className="alert alert-danger">
                        {t(
                            'Alert_JavascriptDisabled',
                            'WARNING: You have JavaScript disabled and will be unable to complete this form.',
                        )}
                        .
                    </div>
                </div>
            </noscript>
        </>
    );

    if (!model.isRegistrationEnabled) {
        return (
            <div className="row">
                <div className="col-xs-12">
                    <div className="alert alert-danger">
                        {t(
                            'DashLabel_NoNewRegistrations',
                            "This site no longer takes new registrations. Please click the logo on the top left to return to the transplant center's site to find the new registration page.",
                        )}
                        .
                    </div>
                </div>
            </div>
        );
    }

    const Treatment = model.hasDynamicRegPage ? DynamicNow : StandardNow;

    return (
        <div>
            <FormProvider {...methods}>
                <Treatment
                    model={model}
                    t={t}
                    onSubmit={handleSubmit(onValid)}
                    isSubmitting={isSubmitting}
                    submitError={submitError}
                    alerts={alerts}
                />

                <NameConfirmationModal
                    show={showNameModal}
                    name={confirmation.name}
                    email={confirmation.email}
                    phone={confirmation.phone}
                    address={confirmation.address}
                    isUsResident={isUsResident}
                    onConfirm={handleConfirmName}
                    onCancel={() => setShowNameModal(false)}
                    t={t}
                />

                {/* GTM noscript fallback. */}
                {model.googleAnalyticsCode && (
                    <noscript>
                        <iframe
                            title="gtm"
                            src={`https://www.googletagmanager.com/ns.html?id=${model.googleAnalyticsCode}`}
                            height={0}
                            width={0}
                            style={{ display: 'none', visibility: 'hidden' }}
                        />
                    </noscript>
                )}
            </FormProvider>
        </div>
    );
};

export default Now;
